import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { filter, get, isEmpty, map } from 'lodash';
import international from 'sort-international';
import { FontIcon, IconButton, MenuItem, SelectField } from 'material-ui';
import { FormattedMessage } from 'react-intl';
import DatePicker from 'react-datepicker';
import { connect } from 'react-redux';
import { Row } from 'react-flexbox-grid';
import pallet from '../../constants/colors';

import Loading from '../../components/Loading';
import PanelMessage from '../../components/panels/PanelMessage';
import { initialize, changePeriod } from './actions';
import KTitle from '../../components/KJRN/KTitle';
import InOutTable from './InOutTable';
import {
  getSettingByKey,
  setSettingByKey,
  // setSettingByKey,
} from '../../utils/localSettings';
import { IN_OUT_FILTERS } from './constants';

const labelStyle = {
  display: 'inline-block',
  padding: 12,
  color: 'white',
  marginRight: 6,
};

const InOut = ({
  inOutInitialize,
  setTimePeriod,
  datesBetween,
  start,
  end,
  error,
  hasError,
  clockTypesById,
  initializeFailed,
  language,
  employees,
  orgCompanyUnits,
  loading,
  initializing,
  initialized,
  inOutFilters,
}) => {
  const [companyUnits, setCompanyUnits] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [clockEntryTypes, setClockEntryTypes] = useState([]);
  const [selectedValues, setSelectedValues] = useState(getSettingByKey(IN_OUT_FILTERS) || {
    orgCompanyUnitId: [0],
    employeeId: [],
    typeId: [],
  });

  const inOutExtraTypes = [
    { id: 'absence', value: language === 'is' ? 'Fjarvera' : 'Absence' },
  ];

  useEffect(() => {
    if (!isEmpty(clockTypesById)) {
      setClockEntryTypes(map(clockTypesById));
    }
  }, [clockTypesById]);

  useEffect(() => {
    if (inOutFilters) {
      setSelectedValues(inOutFilters);
    }
  }, [inOutFilters]);

  useEffect(() => {
    inOutInitialize();
  }, []);

  const filterByType = (data) => {
    const result = [];
    const { typeId } = selectedValues;
    if (!typeId.length) {
      return data;
    }
    map(data, (emp) => {
      let clocksFiltered = [];
      if (typeId.includes('absence')) {
        clocksFiltered = filter(emp.ClockCardEntries, c => !c.Active || (c.Active && typeId.includes(c.ClockCardEntryTypeName_EN)));
      } else {
        clocksFiltered = filter(emp.ClockCardEntries, c => c.Active && typeId.includes(c.ClockCardEntryTypeName_EN));
      }
      if (clocksFiltered.length) {
        result.push({ ...emp, ClockCardEntries: clocksFiltered });
      }
    });
    return result;
  };

  const filterByEmployee = () => {
    const { employeeId } = selectedValues;
    if (employeeId.length) {
      const filtered = filter(employees, e => employeeId.includes(e.EmployeeDetailID));
      return filtered.length ? filtered : employees;
    }
    return employees;
  };

  const filterData = () => {
    if (selectedValues.orgCompanyUnitId.length === 0) {
      setDataSource([]);
    } else {
      const data = filterByEmployee();
      const result = filterByType(data);
      setDataSource(result);
    }
  };

  useEffect(() => {
    filterData();
  }, [selectedValues]);

  useEffect(() => {
    filterData();
  }, [employees]);

  useEffect(() => {
    if (initialized) {
      const nullOption = { OrgCompanyUnitID: null, OrgCompanyUnitName: language === 'is' ? 'Velja allar' : 'Select all' };
      setCompanyUnits([nullOption, ...orgCompanyUnits.sort(international('OrgCompanyUnitName'))]);
    }
  }, [initialized, orgCompanyUnits]);

  if (initializing) return <Loading />;

  if (initializeFailed || hasError) {
    return (
      <PanelMessage
        message={<FormattedMessage id="time.initialize.error" values={{ error }} />}
        iconName="error"
        type="error"
      />
    );
  }

  const isPeriod = moment(end).format('DD/MM/YYYY') === moment(start).add(1, 'month').add(-1, 'day').format('DD/MM/YYYY');

  const isMonth = (
    moment(start).format('DD/MM/YYYY') === moment(start).startOf('month').format('DD/MM/YYYY')
    && moment(end).format('DD/MM/YYYY') === moment(end).endOf('month').format('DD/MM/YYYY')
  );

  const setLastPeriod = () => {
    if (isMonth) {
      setTimePeriod(moment(start).add(-1, 'month'), moment(end).add(-1, 'month').endOf('month'), get(selectedValues, 'orgCompanyUnitId'));
    } else if (isPeriod) {
      setTimePeriod(moment(start).add(-1, 'month'), moment(start).add(-1, 'day'), get(selectedValues, 'orgCompanyUnitId'));
    } else {
      const numberOfDays = moment(end).diff(start, 'days') + 1;
      setTimePeriod(moment(start).add(-numberOfDays, 'days'), moment(end).add(-numberOfDays, 'days'), get(selectedValues, 'orgCompanyUnitId'));
    }
  };

  const setNextPeriod = () => {
    if (isMonth) {
      setTimePeriod(moment(start).add(1, 'month'), moment(end).add(1, 'month').endOf('month'), get(selectedValues, 'orgCompanyUnitId'));
    } else if (isPeriod) {
      setTimePeriod(moment(start).add(1, 'month'), moment(start).add(2, 'month').add(-1, 'day'), get(selectedValues, 'orgCompanyUnitId'));
    } else {
      const numberOfDays = moment(end).diff(start, 'days') + 1;
      setTimePeriod(moment(start).add(numberOfDays, 'days'), moment(end).add(numberOfDays, 'days'), get(selectedValues, 'orgCompanyUnitId'));
    }
  };
  const setStartDate = (date) => {
    setTimePeriod(date, end, get(selectedValues, 'orgCompanyUnitId'));
  };

  const setEndDate = (date) => {
    setTimePeriod(start, date, get(selectedValues, 'orgCompanyUnitId'));
  };

  const getTitle = date => (
    <div style={{ textAlign: 'center' }}>
      <div>
        {date.dateString.substring(0, 5)}
      </div>
      <div>
        {date.dayOfWeek}
      </div>
    </div>
  );

  const dateTable = {
    key: 'TeriaEmployeeCafeteriaPurchaseID',
    columns: [
      { title: <FormattedMessage id="name" />, dataIndex: 'EmployeeName', width: 220 },
      ...map(datesBetween, d => ({
        title: getTitle(d), dataIndex: d.dateString, width: 40, ...d
      })),
    ],
  };

  const onCompanyUnitChange = (unitIds) => {
    let ids = [];
    if (unitIds.includes(null)) {
      if (unitIds[unitIds.length - 1] === null) {
        // if last element is null, then all units are selected
        const allIds = map(companyUnits.slice(1), 'OrgCompanyUnitID');
        ids = [null];
        setTimePeriod(start, end, allIds);
      } else {
        // user selects other unit after selecting all
        ids = filter(unitIds, val => val);
        setTimePeriod(start, end, ids);
      }
    } else {
      ids = unitIds;
      setTimePeriod(start, end, unitIds);
    }
    setSettingByKey(IN_OUT_FILTERS, { ...selectedValues, orgCompanyUnitId: ids });
    setSelectedValues({ ...selectedValues, orgCompanyUnitId: ids });
  };
  const onEmployeeChange = (empId) => {
    setSettingByKey(IN_OUT_FILTERS, { ...selectedValues, employeeId: empId });
    setSelectedValues({ ...selectedValues, employeeId: empId });
  };
  const onTypeChange = (type) => {
    setSettingByKey(IN_OUT_FILTERS, { ...selectedValues, typeId: type });
    setSelectedValues({ ...selectedValues, typeId: type });
  };

  return (
    <React.Fragment>
      <div style={{ maxWidth: 1400, margin: 'auto' }}>
        <div>
          <KTitle containerStyle={{ marginTop: 12 }} key="hb" title={<FormattedMessage id="inOut.title" />} />
        </div>
        <Row middle="xs" center="xs">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: 12,
              justifyContent: 'space-between',
            }}
          >
            <IconButton onClick={setLastPeriod} style={{ marginRight: 12 }}>
              <FontIcon className="material-icons">arrow_back</FontIcon>
            </IconButton>
            <span style={{ fontSize: 18, fontWeight: 400 }}>
              <DatePicker
                className="smallPicker"
                selected={moment(start)}
                onChange={setStartDate}
                dateFormat="DD.MMM YYYY"
                selectsStart
                startDate={moment(start)}
                endDate={moment(end)}
              />
              {' - '}
              <DatePicker
                selected={moment(end)}
                onChange={setEndDate}
                dateFormat="DD.MMM YYYY"
                selectsEnd
                startDate={moment(start)}
                endDate={moment(end)}
                minDate={moment(start)}
              />
            </span>
            <IconButton onClick={setNextPeriod} style={{ marginLeft: 12 }}>
              <FontIcon className="material-icons">arrow_forward</FontIcon>
            </IconButton>
          </div>
        </Row>
        <div style={{ marginTop: 12 }}>
          {map(clockEntryTypes, type => (
            <div key={type.id} style={{ ...labelStyle, backgroundColor: type.color }}>{type.value_short} - {type.value}</div>
          ))}
          <div style={{ ...labelStyle, backgroundColor: pallet.errorColor }}>FV - <FormattedMessage id="inOut.type.absence" /></div>
          <div style={{ ...labelStyle, backgroundColor: pallet.warningColor }}>* - <FormattedMessage id="inOut.type.moreThanOne" /></div>
        </div>
        <Row style={{ maxWidth: 1400, margin: 'auto' }}>
          <SelectField
            multiple
            style={{ width: 360, marginRight: 12 }}
            floatingLabelFixed
            floatingLabelText={<FormattedMessage id="employeeSearch.department" />}
            value={get(selectedValues, 'orgCompanyUnitId').length && get(selectedValues, 'orgCompanyUnitId')}
            onChange={(event, index, value) => onCompanyUnitChange(value)}
          >
            {map(companyUnits, ({ OrgCompanyUnitID, OrgCompanyUnitName }) => (
              <MenuItem
                key={OrgCompanyUnitID}
                value={OrgCompanyUnitID}
                primaryText={OrgCompanyUnitName}
                insetChildren
                checked={get(selectedValues, 'unitId') && get(selectedValues, 'unitId').indexOf(OrgCompanyUnitID) > -1}
              />
            ))}
          </SelectField>
          <SelectField
            multiple
            style={{ width: 280, marginRight: 12 }}
            floatingLabelFixed
            floatingLabelText={<FormattedMessage id="employee" />}
            value={get(selectedValues, 'employeeId')}
            onChange={(event, index, value) => onEmployeeChange(value)}
            disabled={loading}
          >
            {map([...employees], ({ EmployeeDetailID, EmployeeName }) => (
              <MenuItem
                key={EmployeeDetailID}
                value={EmployeeDetailID}
                primaryText={EmployeeName}
                insetChildren
                checked={get(selectedValues, 'employeeId') && get(selectedValues, 'employeeId').indexOf(EmployeeDetailID) > -1}
              />
            ))}
          </SelectField>
          <SelectField
            multiple
            style={{ width: 200 }}
            floatingLabelFixed
            floatingLabelText={<FormattedMessage id="inOut.type" />}
            value={get(selectedValues, 'typeId')}
            onChange={(event, index, value) => onTypeChange(value)}
            disabled={loading}
          >
            {map([...inOutExtraTypes, ...clockEntryTypes], ({ id, value }) => (
              <MenuItem
                key={id}
                value={id}
                primaryText={value}
                insetChildren
                checked={get(selectedValues, 'typeId') && get(selectedValues, 'typeId').indexOf(id) > -1}
              />
            ))}
          </SelectField>
        </Row>
        {datesBetween.length ? <InOutTable table={dateTable} entries={dataSource} /> : null}
      </div>
    </React.Fragment>
  );
};

InOut.propTypes = {
  clockTypesById: PropTypes.PropTypes.shape({}).isRequired,
  datesBetween: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  inOutInitialize: PropTypes.func.isRequired,
  setTimePeriod: PropTypes.func.isRequired,
  hasError: PropTypes.bool.isRequired,
  initializeFailed: PropTypes.bool.isRequired,
  start: PropTypes.shape({}).isRequired,
  end: PropTypes.shape({}).isRequired,
  error: PropTypes.string,
  language: PropTypes.string.isRequired,
  employees: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  orgCompanyUnits: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool.isRequired,
  initializing: PropTypes.bool.isRequired,
  initialized: PropTypes.bool.isRequired,
  inOutFilters: PropTypes.shape({}).isRequired,
};

InOut.defaultProps = {
  error: null,
};

export default connect(
  ({ inOutReducer, languageReducer }) => ({ ...inOutReducer, ...languageReducer }),
  dispatch => ({
    inOutInitialize: () => dispatch(initialize()),
    setTimePeriod: (from, to, unitId) => dispatch(changePeriod(from, to, unitId)),
  }),
)(InOut);



// WEBPACK FOOTER //
// ./src/sites/inOut/InOut.js